import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/scripts/AuthContext";
import { useOrgSocialActor } from "@/scripts/OrgSocialActorContext";
import { useSocialPaths } from "@/lib/socialPaths";
import { Alert, AlertDescription } from "@/components/ui/alert";
import OrganizationAvatar from "@/components/avatars/OrganizationAvatar";
import { API_BASE_URL } from "@/lib/apiBase";

export default function OrgSocialExplorePage() {
  const { accessToken } = useAuth();
  const { orgId, canPublish } = useOrgSocialActor();
  const paths = useSocialPaths();
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${accessToken}` }),
    [accessToken]
  );

  const load = useCallback(async () => {
    if (!accessToken || !orgId) return;
    setLoading(true);
    setError(null);
    try {
      if (canPublish) {
        const res = await axios.get(
          `${API_BASE_URL}/org/${orgId}/social/feed/explore`,
          { headers }
        );
        setOrgs(res.data?.orgs || []);
        setUsers(res.data?.users || []);
      } else {
        const [uRes, oRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/social/explore/users`, { headers }),
          axios.get(`${API_BASE_URL}/social/orgs/explore`, { headers }),
        ]);
        setUsers(uRes.data?.users || []);
        setOrgs(oRes.data?.orgs || []);
      }
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to load Explore"
      );
    } finally {
      setLoading(false);
    }
  }, [accessToken, orgId, canPublish, headers]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 space-y-8">
      <div>
        <h2 className="text-lg font-semibold">Explore</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Discover organizations and people. Follow as your organization.
        </p>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <section className="space-y-3">
        <h3 className="text-sm font-medium">Organizations</h3>
        {orgs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No organizations yet.</p>
        ) : (
          orgs.map((o) => (
            <Link
              key={o.username}
              to={paths.org(o.username)}
              className="flex items-center gap-3 rounded-lg border border-border p-3 hover:bg-muted/40 transition-colors"
            >
              <OrganizationAvatar
                org={{
                  org_id: o.org_id,
                  name: o.display_name,
                  avatar_url: o.avatar_url,
                }}
                className="h-10 w-10"
              />
              <div className="min-w-0">
                <p className="font-medium text-sm truncate">{o.display_name}</p>
                <p className="text-xs text-muted-foreground">@{o.username}</p>
              </div>
            </Link>
          ))
        )}
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-medium">People</h3>
        {users.length === 0 ? (
          <p className="text-sm text-muted-foreground">No people yet.</p>
        ) : (
          users.map((u) => (
            <Link
              key={u.username}
              to={paths.user(u.username)}
              className="block rounded-lg border border-border p-3 hover:bg-muted/40 text-sm transition-colors"
            >
              <span className="font-medium">{u.display_name}</span>{" "}
              <span className="text-muted-foreground">@{u.username}</span>
            </Link>
          ))
        )}
      </section>
    </div>
  );
}
